import { normalizeScopeDateContext } from "../../lib/scopeDate.js";
import { summarizeBudgetRows } from "../../lib/finance/budgetMath.js";
import { filterExpenseRowsThroughToday } from "../../lib/finance/projectionMath.js";
import {
  ANALYSIS_CHART_METRIC_ALL,
  ANALYSIS_CHART_METRIC_CATEGORY
} from "../../lib/settings/profileSettings.js";
import { formatCurrency } from "../../lib/formatters.js";
import { EXPENSE_TYPE_IRREGULAR } from "../../lib/expenseType.js";

const monthLabelKeys = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

export function buildMonthlyExpenseColumns({
  budgetRows = [],
  expenseRows = [],
  metric = ANALYSIS_CHART_METRIC_ALL,
  selectedCategory = "",
  t = (key) => key
} = {}) {
  const months = monthLabelKeys.map((labelKey, index) => ({
    key: `${index + 1}`,
    label: t(labelKey),
    ongoing: 0,
    irregular: 0,
    total: 0
  }));
  const monthlyBudget = metric === ANALYSIS_CHART_METRIC_ALL
    ? budgetRows.reduce((sum, row) => sum + Number(row.ongoingBudget ?? 0), 0)
    : metric === ANALYSIS_CHART_METRIC_CATEGORY
      ? budgetRows
        .filter((row) => String(row.category ?? "").trim() === selectedCategory)
        .reduce((sum, row) => sum + Number(row.ongoingBudget ?? 0), 0)
      : null;

  expenseRows.forEach((row) => {
    const date = String(row.date ?? "");
    const monthIndex = Number(date.slice(5, 7)) - 1;
    if (!Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11) {
      return;
    }

    const amount = Number(row.amount ?? 0);
    if (!Number.isFinite(amount)) {
      return;
    }

    const month = months[monthIndex];
    if (!month) {
      return;
    }

    if (row.type === EXPENSE_TYPE_IRREGULAR) {
      month.irregular += amount;
    } else {
      month.ongoing += amount;
    }

    month.total += amount;
  });

  return months.map((month) => ({
    key: month.key,
    label: month.label,
    valueLabel: month.total > 0 ? formatCurrency(month.total) : "\u00A0",
    title: `${month.label}: ${formatCurrency(month.ongoing)} ${t("ongoing")}, ${formatCurrency(month.irregular)} ${t("irregular")}, ${formatCurrency(month.total)} ${t("total")}`,
    markerValue: Number.isFinite(monthlyBudget) && monthlyBudget > 0 ? monthlyBudget : null,
    segments: [
      {
        key: `${month.key}-irregular`,
        tone: "warning",
        value: month.irregular
      },
      {
        key: `${month.key}-ongoing`,
        tone: "success",
        value: month.ongoing
      }
    ]
  }));
}

export function buildProjectionSeries(rows = [], expenseRows = [], dateInput, t = (key) => key) {
  const dateContext = normalizeScopeDateContext(dateInput);
  const monthlyOngoingBudget = rows.reduce((sum, row) => sum + Number(row.ongoingBudget ?? 0), 0);
  const yearlyIrregularBudget = rows.reduce((sum, row) => sum + Number(row.irregularBudget ?? 0), 0);
  const monthlyExpectedIncrement = monthlyOngoingBudget + (yearlyIrregularBudget / 12);
  const monthlyActualTotals = new Array(12).fill(0);
  const actualExpenseRows = filterExpenseRowsThroughToday(expenseRows, dateContext);

  actualExpenseRows.forEach((row) => {
    const dateText = String(row.date ?? "");
    const year = Number(dateText.slice(0, 4));
    const monthIndex = Number(dateText.slice(5, 7)) - 1;
    const amount = Number(row.amount ?? 0);

    if (!Number.isFinite(amount) || year !== dateContext.scopedYear) {
      return;
    }

    if (!Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11) {
      return;
    }

    monthlyActualTotals[monthIndex] += amount;
  });

  const expectedValues = monthlyActualTotals.map((_, monthIndex) => monthlyExpectedIncrement * (monthIndex + 1));
  const cumulativeActualValues = monthlyActualTotals.reduce((values, amount) => {
    const previous = values.length > 0 ? values[values.length - 1] : 0;
    values.push(previous + amount);
    return values;
  }, []);

  if (!dateContext.isCurrentYear) {
    return {
      labels: monthLabelKeys.map((key) => t(key)),
      series: [
        {
          key: "expected",
          label: t("budget"),
          tone: "danger",
          values: expectedValues
        },
        {
          key: "actual",
          label: t("actual"),
          tone: "success",
          area: true,
          values: cumulativeActualValues
        }
      ]
    };
  }

  const currentMonthIndex = Math.min(Math.max(dateContext.currentMonthIndex, 0), 11);
  const currentMonthDays = new Date(dateContext.scopedYear, currentMonthIndex + 1, 0).getDate();
  const lastCompletedMonthIndex = Math.min(
    dateContext.currentDate >= currentMonthDays ? currentMonthIndex : currentMonthIndex - 1,
    11
  );
  const actualTotalSoFar = lastCompletedMonthIndex >= 0
    ? (cumulativeActualValues[lastCompletedMonthIndex] ?? 0)
    : 0;
  const annualSummary = summarizeBudgetRows(rows, expenseRows, dateContext);
  const projectedYearEndTotal = annualSummary.total.projected;
  const remainingProjectedMonths = Math.max(11 - Math.max(lastCompletedMonthIndex, 0), 0);
  const projectedMonthlyIncrement = remainingProjectedMonths > 0
    ? (projectedYearEndTotal - actualTotalSoFar) / remainingProjectedMonths
    : 0;
  const actualValues = cumulativeActualValues.map((value, monthIndex) => (
    monthIndex <= lastCompletedMonthIndex ? value : null
  ));
  const projectedValues = cumulativeActualValues.map((value, monthIndex) => {
    const anchorMonthIndex = Math.max(lastCompletedMonthIndex, 0);
    if (monthIndex < anchorMonthIndex) {
      return null;
    }

    if (monthIndex === anchorMonthIndex) {
      return actualTotalSoFar;
    }

    if (monthIndex === 11) {
      return projectedYearEndTotal;
    }

    return actualTotalSoFar + projectedMonthlyIncrement * (monthIndex - anchorMonthIndex);
  });

  return {
    labels: monthLabelKeys.map((key) => t(key)),
    series: [
      {
        key: "expected",
        label: t("budget"),
        tone: "danger",
        values: expectedValues
      },
      {
        key: "actual",
        label: t("actual"),
        tone: "success",
        area: true,
        values: actualValues
      },
      {
        key: "projected",
        label: t("projected"),
        tone: "success",
        area: true,
        dashed: true,
        pointStyle: "outline",
        values: projectedValues
      }
    ]
  };
}
